import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useHotel } from "@/hooks/useHotel";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Filter, Loader2, RefreshCw, Zap } from "lucide-react";
import { PageState } from "@/components/common/PageState";
import { SyncJobsTable } from "./components/SyncJobsTable";
import { SyncJobDetailDialog } from "./components/SyncJobDetailDialog";
import { SelectiveSyncDialog } from "./components/SelectiveSyncDialog";
import { SyncJobsFiltersBar } from "./components/SyncJobsFilters";
import { TablePagination } from "@/components/common/TablePagination";
import { ExportSyncJobsButton } from "./components/ExportSyncJobsButton";
import {
  useSyncJobs,
  useTriggerFullSync,
  useTriggerIncrementalSync,
  useCancelSyncJob,
} from "./hooks/pms.hooks";
import { SyncJob, SyncJobType, SyncJobsFilters } from "@/types/pms";
import {
  getSyncTriggerErrorMessage,
  isBrowserOffline,
} from "./utils/syncTriggerErrors";
import {
  DEFAULT_SYNC_JOBS_FILTERS,
  parseSyncJobsFiltersFromParams,
  syncJobsFiltersToParams,
} from "./utils/syncJobsFilters";

export const SyncDashboardPage = () => {
  const { hotelId } = useHotel();
  const { t } = useTranslation();

  const [selectedJob, setSelectedJob] = useState<SyncJob | null>(null);
  const [cancelJobId, setCancelJobId] = useState<string | null>(null);
  const [selectiveSyncOpen, setSelectiveSyncOpen] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const filters = useMemo<SyncJobsFilters>(
    () => parseSyncJobsFiltersFromParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );

  const handleFiltersChange = (next: SyncJobsFilters) => {
    setSearchParams(syncJobsFiltersToParams(next), { replace: true });
  };

  // Filter/sort/pageSize changes reset to page 1.
  const handleFilterFieldChange = (next: SyncJobsFilters) => {
    handleFiltersChange({ ...next, page: 1 });
  };

  const handlePageChange = (page: number) => {
    handleFiltersChange({ ...filters, page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    handleFiltersChange({ ...filters, pageSize, page: 1 });
  };

  const handleResetFilters = () => {
    setSearchParams(syncJobsFiltersToParams(DEFAULT_SYNC_JOBS_FILTERS), {
      replace: true,
    });
  };

  const hasActiveFilters = Boolean(
    filters.from || filters.to || filters.status || filters.type,
  );

  const { data, isLoading, isError } = useSyncJobs(filters);
  const jobs = data?.items ?? [];

  const triggerFull = useTriggerFullSync();
  const triggerIncremental = useTriggerIncrementalSync();
  const cancelJob = useCancelSyncJob();

  if (!hotelId) {
    return <PageState text={t("pms.hotelNotSelected")} />;
  }

  if (isLoading) {
    return <PageState text={t("pms.loading")} />;
  }

  if (isError) {
    return <PageState text={t("pms.loadingError")} tone="error" />;
  }

  const handleTriggerSync = (type: SyncJobType) => {
    if (isBrowserOffline()) {
      toast.error(t("pms.sync.networkError"));
      return;
    }

    const mutation =
      type === SyncJobType.FULL ? triggerFull : triggerIncremental;
    mutation.mutate(undefined, {
      onSuccess: () => toast.success(t("pms.sync.triggerSuccess")),
      onError: (error) => {
        toast.error(getSyncTriggerErrorMessage(error, t));
      },
    });
  };

  const handleCancelJob = (jobId: string) => {
    setCancelJobId(jobId);
  };

  const confirmCancel = () => {
    if (!cancelJobId) return;
    cancelJob.mutate(cancelJobId, {
      onSuccess: () => {
        toast.success(t("pms.sync.cancelSuccess"));
        setCancelJobId(null);
      },
      onError: () => {
        toast.error(t("pms.sync.cancelError"));
        setCancelJobId(null);
      },
    });
  };

  return (
    <div className="flex flex-col gap-6 p-12">
      <Card>
        <CardHeader>
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
            <CardTitle>{t("pms.sync.title")}</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="new_default"
                disabled={triggerFull.isPending}
                onClick={() => handleTriggerSync(SyncJobType.FULL)}
              >
                {triggerFull.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {t("pms.sync.fullSync")}
              </Button>
              <Button
                variant="new_outline"
                onClick={() => setSelectiveSyncOpen(true)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {t("pms.sync.selectiveSync")}
              </Button>
              <Button
                variant="new_outline"
                disabled={triggerIncremental.isPending}
                onClick={() => handleTriggerSync(SyncJobType.INCREMENTAL)}
              >
                {triggerIncremental.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Zap className="mr-2 h-4 w-4" />
                )}
                {t("pms.sync.incrementalSync")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <SyncJobsFiltersBar
              filters={filters}
              onChange={handleFilterFieldChange}
            />
            <ExportSyncJobsButton filters={filters} disabled={isLoading} />
          </div>
          <div className="overflow-x-auto border-t pt-4">
            <SyncJobsTable
              jobs={jobs}
              filters={filters}
              hasActiveFilters={hasActiveFilters}
              onFiltersChange={handleFilterFieldChange}
              onView={(job) => setSelectedJob(job)}
              onCancel={handleCancelJob}
              onResetFilters={handleResetFilters}
              isCancelling={cancelJob.isPending}
            />
          </div>
          {data && data.total > 0 && (
            <TablePagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      <SyncJobDetailDialog
        job={selectedJob}
        open={selectedJob !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedJob(null);
        }}
      />

      <SelectiveSyncDialog
        open={selectiveSyncOpen}
        onOpenChange={setSelectiveSyncOpen}
      />

      <Dialog
        open={cancelJobId !== null}
        onOpenChange={(open) => {
          if (!open) setCancelJobId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pms.sync.cancelConfirmTitle")}</DialogTitle>
            <DialogDescription>
              {t("pms.sync.cancelConfirmDescription")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="new_outline" onClick={() => setCancelJobId(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="new_destructive"
              disabled={cancelJob.isPending}
              onClick={confirmCancel}
            >
              {t("pms.sync.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
